import { pageWithoutLocale } from "./index";

const STORAGE_KEY = "xltools-contact-tags";

const POPUP_CALENDAR_KEYWORDS = [
  "popup-calendar",
  "date-picker",
  "calendar",
  "insert-date",
  "insert-time",
  "date-time",
];

const DATA_PREP_KEYWORDS = [
  "data-prep",
  "data-cleansing",
  "data-clean",
  "clean",
  "cleanup",
  "unpivot",
  "split",
  "merge",
  "combine",
  "columns-match",
  "convert-numbers-stored-as-text",
  "stored-as-text",
  "replace-characters",
  "delete-characters",
  "add-characters",
  "change-text-case",
  "combine-cells",
  "add-text-to-cells",
  "export-to-csv",
  "text",
];

const VERSION_CONTROL_KEYWORDS = [
  "version-control",
  "track-changes",
  "compare",
  "diff",
  "rollback",
  "revision",
  "audit",
  "history",
];

const AUTOMATION_KEYWORDS = [
  "automation",
  "automate",
  "sql",
  "mysql",
  "order-processing",
  "vsto",
  "vba",
  "js-add-ins",
  "office-js",
  "online-app",
  "online-sales",
  "excel-apps",
  "custom-shortcuts",
];

const GPT_KEYWORDS = ["gpt", "ai", "copilot"];

const matchesKeyword = (path, keywords) =>
  keywords.some((keyword) => path.includes(keyword));

const safeWindow = () => typeof window !== "undefined";

const parseTags = (value) => {
  if (!value) {
    return [];
  }

  try {
    const parsed = JSON.parse(value);

    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed.filter(
      (item) => typeof item === "string" && item.trim().length > 0,
    );
  } catch (error) {
    return [];
  }
};

const uniqueTags = (tags) => [...new Set(tags.filter(Boolean))];

const normalizeTagList = (value) => {
  if (!value) {
    return [];
  }

  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim()).filter(Boolean);
  }

  return String(value)
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
};

export const normalizeTrackedPath = (path = "/") => {
  const [pathname] = String(path).split(/[?#]/);
  const pagePath = pageWithoutLocale(pathname || "/");

  if (pagePath === "/") {
    return "/";
  }

  return pagePath.endsWith("/") ? pagePath : `${pagePath}/`;
};

export const resolvePageTag = (rawPath) => {
  const path = normalizeTrackedPath(rawPath).toLowerCase();

  if (path === "/") {
    return "Home";
  }

  if (path.startsWith("/download-free-trial/")) {
    return "Free Trial";
  }

  if (path.startsWith("/purchase/") || path.startsWith("/purchase-upgrade/")) {
    return "Pricing";
  }

  if (path.startsWith("/contacts/")) {
    return "Contact";
  }

  if (
    path.startsWith("/support/") ||
    path.includes("release-notes") ||
    path.includes("troubleshooting")
  ) {
    return "Support";
  }

  if (matchesKeyword(path, GPT_KEYWORDS)) {
    return "GPT Functions";
  }

  if (matchesKeyword(path, POPUP_CALENDAR_KEYWORDS)) {
    return "Popup Calendar";
  }

  if (matchesKeyword(path, VERSION_CONTROL_KEYWORDS)) {
    return "Version Control";
  }

  if (matchesKeyword(path, AUTOMATION_KEYWORDS)) {
    return "Automation";
  }

  if (matchesKeyword(path, DATA_PREP_KEYWORDS)) {
    return "Data Prep";
  }

  if (path.startsWith("/services/") || path.startsWith("/services-")) {
    return "Services";
  }

  if (path.startsWith("/blog/")) {
    return "Blog";
  }

  return null;
};

export const getTrackedTags = () => {
  if (!safeWindow()) {
    return [];
  }

  return parseTags(window.localStorage.getItem(STORAGE_KEY));
};

const persistTrackedTags = (tags) => {
  if (!safeWindow()) {
    return;
  }

  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(uniqueTags(tags)));
};

export const trackPageTag = (path) => {
  if (!safeWindow()) {
    return [];
  }

  const pageTag = resolvePageTag(path);
  const nextTags = uniqueTags([...getTrackedTags(), pageTag]);

  persistTrackedTags(nextTags);

  return nextTags;
};

export const appendTrackedTags = (payload) => {
  const trackedTags = getTrackedTags();

  if (!payload || trackedTags.length === 0) {
    return payload;
  }

  if (payload instanceof FormData) {
    const mergedTags = uniqueTags([
      ...normalizeTagList(payload.getAll("Tags")),
      ...trackedTags,
    ]);

    payload.delete("Tags");
    mergedTags.forEach((tag) => payload.append("Tags", tag));

    return payload;
  }

  const mergedTags = uniqueTags([
    ...normalizeTagList(payload.tags),
    ...trackedTags,
  ]);

  return {
    ...payload,
    tags: mergedTags,
  };
};
